import postRequests from "api/post";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLoyaltyUser } from "features/slices/loyaltySlice";
import { MdOutlineStoreMallDirectory } from "react-icons/md";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { MdVisibilityOff, MdVisibility } from "react-icons/md";
import Blob from "./Blob";
import { Loader } from "components";
import { SocialMediaIcons } from "components/SocialMedia";
import PropTypes from "prop-types";
import { AUTHENTICATED_USER } from "constants/keys";
import "../loyalty.css";

LoyaltyLogin.propTypes = {
  merchantDetails: PropTypes.object,
  customerDetails: PropTypes.object,
  customerPhoneNumberFromURL: PropTypes.string,
  recentOrderId: PropTypes.string,
  handleShowLogin: PropTypes.func,
  handleShowRegister: PropTypes.func,
  merchantTheme: PropTypes.object,
};

LoyaltyLogin.defaultProps = {
  merchantDetails: {},
  customerDetails: {},
  customerPhoneNumberFromURL: "",
  recentOrderId: "",
  handleShowRegister: () => {},
  merchantTheme: {},
};

export default function LoyaltyLogin(props) {
  const {
    merchantDetails,
    customerDetails,
    customerPhoneNumberFromURL,
    recentOrderId,
    handleShowRegister,
    merchantTheme,
  } = props;
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [merchantId, setMerchantId] = useState(
    merchantDetails?.merchant_id || null
  );
  const [orderId, setOrderId] = useState(recentOrderId || null);
  const [phone, setPhone] = useState(
    customerPhoneNumberFromURL || customerDetails?.customer_phone || ""
  );
  const [showPassword, setShowPassword] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showPlaceholderIcon, setShowPlaceholderIcon] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const phoneFromURL = queryParams.get("phone");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      phone: phone,
      password: "",
    },
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      let user = null;
      try {
        user = sessionStorage.getItem("LOYALTY_USER");
      } catch (e) {
        user = null;
      }

      let phoneValue =
        customerPhoneNumberFromURL || customerDetails?.customer_phone || "";
      if (user) {
        try {
          const parsed = JSON.parse(user);
          setMerchantId(parsed?.merchantId);
          setOrderId(parsed?.recentOrderId);
          !customerPhoneNumberFromURL &&
            (phoneValue = parsed?.customer_phone || parsed._phone);
        } catch (e) {
          setMerchantId(merchantDetails?.merchant_id || null);
          phoneValue = customerDetails?.customer_phone || "";
        }
      } else {
        setMerchantId(merchantDetails?.merchant_id || null);
        phoneValue = customerDetails?.customer_phone || "";
      }
      setPhone(phoneValue);
      setValue("phone", phoneValue);

      if (merchantDetails) {
        sessionStorage.setItem("MERCHANT", JSON.stringify(merchantDetails));
        sessionStorage.setItem("MERCHANT_THEME", JSON.stringify(merchantTheme));
      }
      if (customerDetails) {
        const userData = {
          ...customerDetails,
          merchantId: merchantDetails?.merchant_id,
          recentOrderId,
          merchantCode: merchantDetails?.merchant_code,
        };
        customerDetails?.customer_has_account
          ? setIsNewUser(false)
          : setIsNewUser(true);
        sessionStorage.setItem("LOYALTY_USER", JSON.stringify(userData));
        dispatch(setLoyaltyUser(userData));
      }
    }
    setIsInitialized(true);
  }, [merchantDetails, customerDetails]);

  const onSubmit = async (data) => {
    if (!merchantId) {
      toast.error("Merchant information is missing. Please check the URL.");
      return;
    }
    setLoading(true);
    try {
      const encodedNumber = encodeURIComponent(data.phone);
      const resData = await postRequests.loginUser({
        username: encodedNumber,
        password: data.password,
        merchant: merchantId,
      });

      if (Number(resData?.status) === 0) {
        try {
          const userData = {
            ...resData,
            recentOrderId: recentOrderId ?? orderId,
            merchantId,
          };

          sessionStorage.setItem("LOYALTY_USER", JSON.stringify(userData));
          dispatch(setLoyaltyUser(userData));

          toast.error(resData.message || "Login successful!");
          localStorage.setItem(AUTHENTICATED_USER, JSON.stringify(userData));
          window.location.href = `${merchantTheme?.slug}/dashboard`;
        } catch (tokenError) {
          toast.error(
            "Login successful but session initialization failed. Please try again."
          );
        }
      } else {
        toast.error(resData?.message || "Login failed.");
      }
    } catch (err) {
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isInitialized) {
    return <Loader text="Loading..." />;
  }

  return (
    <div
      className="loyalty-section min-h-screen flex flex-col lg:flex-row bg-white"
      data-testid="loyalty-login"
    >
      <Blob />

      <div
        className={`flex justify-center items-center lg:hidden inset-0 ${
          merchantTheme?.theme || "bg-brandBlue"
        } z-0 w-full py-20 px-10`}
        style={{
          backgroundImage:
            merchantTheme?.auth_banner !==
            "https://payments.ipaygh.com/app/webroot/img/stores/null"
              ? `url("${merchantTheme?.auth_banner}")`
              : 'url("/svgs/gift.svg")',
          backgroundSize: merchantTheme?.auth_banner ? "cover" : "500px 500px",
          backgroundColor: merchantTheme?.theme ?? null,
          backgroundPosition: merchantTheme?.auth_banner ? "center" : "",
        }}
      >
        <div className="absolute inset-0 bg-black-900 bg-opacity-50 z-0"></div>

        <div className="w-12 h-12 bg-gray-200 border-2 border-gray-200 rounded-full flex items-center justify-center mr-4 overflow-hidden z-50">
          {merchantDetails?.merchant_logo && !showPlaceholderIcon ? (
            <img
              src={merchantDetails.merchant_logo}
              alt={`${merchantDetails?.merchant_name || "Merchant"} logo`}
              className="w-full h-full object-cover"
              onError={(e) => setShowPlaceholderIcon(true)}
            />
          ) : (
            <MdOutlineStoreMallDirectory
              size={50}
              color={merchantTheme?.theme}
              className={`p-2 ${merchantTheme?.theme || "text-gray-500"}`}
            />
          )}
        </div>

        <h2 className="text-2xl font-extrabold mb-3 tracking-tight text-gray-100 drop-shadow text-center">
          {merchantDetails?.merchant_name}
        </h2>
      </div>

      <div className="flex-1 flex justify-center lg:items-center bg-white p-8 z-50 bg-white-A700">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold mt-8 mb-2 text-gray-800 text-center">
            Loyalty Login
          </h2>
          <p className="text-center text-gray-500 mb-2">
            Sign in to access your loyalty rewards and account.
          </p>
          <div
            onClick={() => navigate(`/${merchantTheme?.slug}/how-it-works`)}
            className="text-gray-600 text-sm underline hover:cursor-pointer hover:text-brandBlue hover:underline transition-colors flex justify-center items-center mb-8"
          >
            <p className="text-center">How it works?</p>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-semibold mb-1"
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brandBlue transition-colors ${
                  errors.phone
                    ? "border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50"
                    : "border-gray-300"
                } ${phoneFromURL ? "bg-gray-200" : ""}`}
                placeholder="Enter Mobile Number"
                {...register("phone", {
                  required: "Mobile number is required",
                })}
                disabled={loading || phoneFromURL}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold mb-1"
              >
                {isNewUser ? "New Password" : "Password"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brandBlue transition-colors pr-10 ${
                    errors.password
                      ? "border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder={
                    isNewUser ? "Enter new password" : "Enter password"
                  }
                  {...register("password", {
                    required: "Password is required",
                  })}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  data-testid="toggle-password"
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.password.message}
                </p>
              )}

              <div className="flex justify-end">
                <a
                  href={`/${merchantTheme?.slug}/forgot-password`}
                  className="text-right text-gray-600 text-sm underline hover:cursor-pointer hover:text-brandBlue hover:underline transition-colors"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              className={`w-full py-3 rounded font-semibold text-lg mt-2 transition-all duration-200 ${
                merchantTheme?.theme || "bg-brandBlue"
              } text-gray-100`}
              style={{ backgroundColor: merchantTheme?.theme }}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Logging in...
                </div>
              ) : (
                "Login"
              )}
            </button>

            <div className="flex flex-col items-center mt-2">
              <span
                onClick={handleShowRegister}
                className={`w-full py-3 rounded font-semibold text-lg text-center mt-2 transition-all duration-200 ${
                  merchantTheme?.theme ||
                  "bg-blue-100 text-brandBlue hover:bg-blue-200 "
                } cursor-pointer`}
                style={{
                  backgroundColor: "white",
                  color: merchantTheme?.theme,
                  borderColor: merchantTheme?.theme,
                  borderWidth: "1px",
                }}
              >
                Join Rewards
              </span>
            </div>
          </form>
          {/* Social Media Icons */}
          <SocialMediaIcons
            socialMediaData={merchantTheme?.social_media}
            merchantTheme={merchantTheme}
          />
        </div>
      </div>
    </div>
  );
}
